import { computed, Ref } from 'vue';
import * as yup from 'yup';
import { getRequestedAmountValidation, getCertifiedAmountValidation } from '@/composables/validationUtils';
import { Draw, ServiceType, DrawStatuses, DrawStatusesToAllowUpdateCertMethod, MinimumDrawAmountResponse, MinimumDrawAmount, LoanDetails } from '@/models/loans';
import { EnrollmentTerm } from "@/types/school";
import enrollmentStatuses from '@/models/enrollmentStatuses';

export interface ValidationData {
    selectedDraw: Ref<Draw | null>;
    mode: Ref<'view' | 'add' | 'edit'>;
    availableToDrawAmount: Ref<number | null>;
    drawStatusOptions: Ref<ServiceType[]>;
    certificationMethodOptions: Ref<ServiceType[]>;
    schoolLevelOptions: Ref<ServiceType[]>;
    enrollmentTermOptions: Ref<EnrollmentTerm[]>;
		minimumDrawAmount: Ref<MinimumDrawAmountResponse | null>;
		draws: Ref<Draw[]>;
		loanDetails: Ref<LoanDetails>;
}

export const enrollmentStatusOptions: ServiceType[] = enrollmentStatuses.map((status) => ({
    id: status.value,
    name: status.label
}));

const maxRequestedAmount = 99999.99;

export function useDrawFormFields(data: ValidationData) {
		const skipKeys = [
			'school',
			'sequenceNumber',
			'schoolCode',
			'schoolBranch',
			'sequence',
      'createdAtUtc',
      'createdBy',
			'parentAttestation',
			'parentConsent'
		];

		const keysToRename = [
			{key: "requestedAmountFormatted", rename: "Requested Amount"},
			{key: "certifiedAmountFormatted", rename: "Certified Amount"},
			{key: "enrollmentTerm", rename: "Selected Enrollment Term"},
			{key: "schoolName", rename: "School"},
		]

		const minimum = computed<MinimumDrawAmount>(() => {
			if (data.draws.value.length === 0) {
				return {
					amount: data.minimumDrawAmount?.value?.minimumInitialDrawAmount || 1000, 
					display: data.minimumDrawAmount?.value?.minimumInitialDrawAmountDisplay || '$1,000'
				};
			}

			return {
				amount: data.minimumDrawAmount?.value?.minimumSubsequentDrawAmount || 250,
				display: data.minimumDrawAmount?.value?.minimumSubsequentDrawAmountDisplay || '$250'
			}
		});
		
		const showCertifiedAmountField = computed<boolean>(() => {
			const requestedAmount = data.selectedDraw.value?.requestedAmount;

			if (requestedAmount && requestedAmount !== null && requestedAmount !== undefined && requestedAmount > 0) {
				return true;
			} else {
				return false;
			}
		});

    const formFields = computed(() => {
        const fields = [
            {
                name: 'school',
                label: 'School',
                type: 'school-select',
                value: {
                    schoolDOEId: data.selectedDraw.value?.schoolCode || '',
                    schoolBranchDOEId: data.selectedDraw.value?.schoolBranch || '',
                    schoolDetailsId: data.selectedDraw.value?.schoolDetailsId || 0
                },
                width: 'col-4',
                showOnAdd: true,
                showOnEdit: true,
                disabledOnEdit:
                    data.selectedDraw.value?.drawStatusTypeId ===
                        DrawStatuses.CertificationPending ||
                        data.selectedDraw.value?.drawStatusTypeId ===
                        DrawStatuses.SentForCertification
                        ? false
                        : true,
            },
            {
                name: 'schoolLevelTypeId',
                label: 'Grade Level',
                type: 'select',
                value: data.selectedDraw.value?.schoolLevelTypeId,
                width: 'col-4',
                options: data.schoolLevelOptions.value,
                showOnAdd: true,
                showOnEdit: true,
                disabledOnEdit: false
            },
            {
                name: 'certificationMethodId',
                label: 'Certification Method',
                type: 'select',
                value: data.selectedDraw.value?.certificationMethodId,
                width: 'col-4',
                options: data.certificationMethodOptions.value,
                showOnAdd: false,
                showOnEdit: true,
                disabledOnEdit: !DrawStatusesToAllowUpdateCertMethod.includes(Number(data.selectedDraw.value?.drawStatusTypeId) as DrawStatuses)
            },
            {
                name: 'enrollmentStatus',
                label: 'Enrollment Status',
                type: 'select',
                value: data.selectedDraw.value?.enrollmentStatus,
                width: 'col-4',
                options: enrollmentStatusOptions,
                showOnAdd: true,
                showOnEdit: true,
                disabledOnEdit: false
            },
            {
                name: 'enrollmentTermId',
                label: 'Selected Enrollment Term',
                type: 'select',
                value: data.selectedDraw.value?.enrollmentTermId,
                width: 'col-4',
                options: getFilteredEnrollmentTerms(data).value,
                showOnAdd: true,
                showOnEdit: true,
                disabledOnEdit: true
            },
            {
                name: 'drawStatusTypeId',
                label: 'Draw Status',
                type: 'select',
                value: data.selectedDraw.value?.drawStatusTypeId,
                width: 'col-4',
                options: getFilteredDrawStatuses(data).value,
                showOnAdd: false,
                showOnEdit: true,
                disabledOnEdit: false
            },
            {
                name: 'requestedAmount',
                label: 'Requested Amount',
                type: 'money',
                value: data.selectedDraw.value?.requestedAmount,
                width: 'col-4',
                showOnAdd: true,
                showOnEdit: true,
                disabledOnEdit: false,
            },
						{
                name: 'certifiedAmount',
                label: 'Certified Amount',
                type: 'money',
                value: data.selectedDraw.value?.certifiedAmount,
                width: 'col-4',
                showOnAdd: showCertifiedAmountField.value,
                showOnEdit: showCertifiedAmountField.value,
                disabledOnEdit: false,
            },
            {
                name: 'cluid',
                label: 'CLUID',
                type: 'string',
                value: data.selectedDraw.value?.cluid,
                width: 'col-4',
                showOnAdd: false,
                showOnEdit: true,
                disabledOnEdit: false
            },
            {
                name: 'sequenceNumber',
                label: 'Sequence',
                type: 'string',
                value: data.selectedDraw.value?.sequenceNumber,
                width: 'col-4',
                showOnAdd: false,
                showOnEdit: true,
                disabledOnEdit: true
            },
            {
                name: 'anticipatedGraduationDate',
                label: 'Anticipated Graduation Date',
                type: 'date',
                value: data.selectedDraw.value?.anticipatedGraduationDate,
                width: 'col-4',
                showOnAdd: true,
                showOnEdit: true,
                disabledOnEdit: false
            },
            {
                name: 'loanPeriodStartDate',
                label: 'Loan Period Start Date',
                type: 'date',
                value: data.selectedDraw.value?.loanPeriodStartDate,
                width: 'col-4',
                showOnAdd: false,
                showOnEdit: true,
                disabledOnEdit: false
            },
            {
                name: 'loanPeriodEndDate',
                label: 'Loan Period End Date',
                type: 'date',
                value: data.selectedDraw.value?.loanPeriodEndDate,
                width: 'col-4',
                showOnAdd: false,
                showOnEdit: true,
                disabledOnEdit: false
            },
            {
                name: 'createdAtUtc',
                label: 'Created On',
                type: 'date',
                value: data.selectedDraw.value?.createdAtUtc,
                width: 'col-4',
                showOnAdd: false,
                showOnEdit: false,
                disabledOnEdit: true
            },
            {
                name: 'createdBy',
                label: 'Created By',
                type: 'string',
                value: data.selectedDraw.value?.createdBy,
                width: 'col-4',
                showOnAdd: false,
                showOnEdit: false,
                disabledOnEdit: true
            },
        ];

        return fields.filter((field) => {
            if (data.mode.value === 'add') {
                return field.showOnAdd !== false;
            } else if (data.mode.value === 'edit') {
                return field.showOnEdit !== false;
            }

            return true;
        });
    });

    const addSchema = computed(() => {
        return yup.object({
            school: yup
                .object()
                .shape({
                    schoolDOEId: yup.string().required('School selection is required'),
                    schoolBranchDOEId: yup.string().required('School selection is required')
                })
                .typeError('School selection is required'),
            schoolLevelTypeId: yup
                .number()
                .typeError('Grade Level is invalid')
                .required('Grade Level is required')
                .min(1),
            enrollmentStatus: yup
                .number()
                .typeError('Enrollment Status is invalid')
                .required('Enrollment Status is required')
                .min(1)
                .test(
                    'less-than-half-for-summer-term',
                    'Less than Half is only allowed for Summer terms',
                    function (value) {
                        const enrollmentTermId = this.parent.enrollmentTermId;
                        const selectedTerm = data.enrollmentTermOptions.value.find(
                            (term) => term.id === enrollmentTermId
                        );
                        const isSummerTerm = selectedTerm?.name?.toLowerCase().includes('summer');

                        return value !== 3 || isSummerTerm;
                    }
                ),
            enrollmentTermId: yup
                .number()
                .typeError('Enrollment Term is invalid')
                .required('Enrollment Term is required')
                .min(1),
            requestedAmount: getRequestedAmountValidation(maxRequestedAmount, () => data.availableToDrawAmount.value, minimum.value),
						certifiedAmount: getCertifiedAmountValidation(()=> data.selectedDraw.value?.requestedAmount, () => data.selectedDraw.value?.requestedAmountFormatted || ""),
            anticipatedGraduationDate: yup
                .date()
                .typeError('Anticipated Graduation Date is invalid')
                .required('Anticipated Graduation Date is required')
                .test(
                    'date-before-term-start',
                    'Anticipated Graduation Date cannot be earlier than the Loan Period End Date',
                    function (value) {
                        const loanPeriodEndDate = data.selectedDraw.value?.loanPeriodEndDate ?  new Date(data.selectedDraw.value?.loanPeriodEndDate) : null;
                        return loanPeriodEndDate ? value >= loanPeriodEndDate : true;
                    }
                ),
						parentConsent: yup
							.boolean()
							.nullable()
							.test(
								"is-valid",
								"You must check the box to continue.",
								function (value) {
									if (!data.loanDetails.value.isParentLOC) {
										return true;
									}

									return value !== undefined && value !== null && value !== false;
								}
							),
				});
    });

    const editSchema = computed(() => {
        return yup.object({
            schoolLevelTypeId: yup
                .number()
                .typeError('Grade Level is invalid')
                .required('Grade Level is required')
                .min(1),
            drawStatusTypeId: yup
                .number()
                .typeError('Draw Status is invalid')
                .required('Draw Status is required')
                .min(1),
            enrollmentStatus: yup
                .number()
                .typeError('Enrollment Status is invalid')
                .required('Enrollment Status is required')
                .min(1),
            requestedAmount: getRequestedAmountValidation(maxRequestedAmount, () => data.availableToDrawAmount.value, minimum.value),
						certifiedAmount: getCertifiedAmountValidation(()=> data.selectedDraw.value?.requestedAmount, () => data.selectedDraw.value?.requestedAmountFormatted || ""),
						anticipatedGraduationDate: yup
                .date()
                .typeError('Anticipated Graduation Date is invalid')
                .required('Anticipated Graduation Date is required')
                .test(
                    'date-before-term-start',
                    'Anticipated Graduation Date cannot be earlier than the Loan Period End Date',
                    function (value) {
                        const loanPeriodEndDate = data.selectedDraw.value?.loanPeriodEndDate ?  new Date(data.selectedDraw.value?.loanPeriodEndDate) : null;
                        return loanPeriodEndDate ? value >= loanPeriodEndDate : true;
                    }
                ),
            cluid: yup
                .string()
                .required("CLUID is required")
                .matches(
                    /^[a-zA-Z0-9]+$/,
                    'CLUID can only contain letters and numbers')
        });
    });

    const validationSchema = computed(() => {
        switch (data.mode.value) {
            case 'add':
                return addSchema.value;
            case 'edit':
                return editSchema.value;
            default:
                return yup.object({});
        }
    });

    return {
        formFields,
        validationSchema,
        skipKeys,
        keysToRename
    };
}

function getFilteredEnrollmentTerms(data: ValidationData) {
    return computed(() => {
        if (data.mode.value === 'view') {
            return data.enrollmentTermOptions.value;
        }

        const today = new Date();

        const filteredOptions = data.enrollmentTermOptions.value.filter((term) => {
            const visibleStartDate = term.systemStartDate ? new Date(term.systemStartDate) : null;
            const visibleEndDate = term.systemEndDate ? new Date(term.systemEndDate) : null;

            const datesAreValid =
                visibleStartDate &&
                visibleEndDate &&
                !isNaN(visibleStartDate.getTime()) &&
                !isNaN(visibleEndDate.getTime());

            const datesAreInRange =
                visibleStartDate && visibleEndDate && visibleStartDate <= today && visibleEndDate >= today;

            return datesAreValid && datesAreInRange;
        });

        if (data.mode.value === 'edit' && data.selectedDraw.value?.enrollmentTermId !== null) {
            const selectedOption = data.enrollmentTermOptions.value.find(
                (term) => term.id === data.selectedDraw.value?.enrollmentTermId
            );

            if (selectedOption && !filteredOptions.includes(selectedOption)) {
                filteredOptions.push(selectedOption);
            }
        }

        return filteredOptions;
    });
}

function getFilteredDrawStatuses(data: ValidationData) {
    return computed(() => {
        if (data.mode.value === 'view') {
            return data.drawStatusOptions.value;
        }

        const filteredOptions = data.drawStatusOptions.value;

        if (data.mode.value === 'edit' && data.selectedDraw.value?.drawStatusTypeId !== null) {
            const selectedOption = data.drawStatusOptions.value.find(
                (status) => status.id === data.selectedDraw.value?.drawStatusTypeId
            );

            if (selectedOption && !filteredOptions.includes(selectedOption)) {
                filteredOptions.push(selectedOption);
            }
        }

        return filteredOptions;
    });
}